/* ═══════════════════════════════════════════
   RISE Pilot Contest — 공용 목업 데이터
   Phase 3-A / 3-B / 3-D / 3-E 가 모두 import
   실제 Firestore 연동은 후속 PR 에서 진행
   ═══════════════════════════════════════════ */

/**
 * 공모전 핵심 날짜 (KST 기준)
 * - 접수 마감까지: submission
 * - 투표 마감까지: voting
 * - 이후: result
 */
export const CONTEST_DATES = {
  SUBMIT_START_KST: '2026-09-01T00:00:00+09:00',
  SUBMIT_END_KST: '2026-09-30T23:59:59+09:00',
  VOTE_START_KST: '2026-10-01T00:00:00+09:00',
  VOTE_END_KST: '2026-10-31T23:59:59+09:00',
  AWARD_KST: '2026-11-14T00:00:00+09:00',
};

/**
 * 현재 공모전 단계 판단 (KST 기준)
 * @param {Date} now 현재 시간 (기본: new Date())
 * @param {'submission'|'voting'|'result'} override URL 쿼리 파라미터로 강제 전환
 */
export const getContestPhase = (now = new Date(), override = null) => {
  if (override === 'submission' || override === 'voting' || override === 'result') {
    return override;
  }
  const submitEnd = new Date(CONTEST_DATES.SUBMIT_END_KST);
  const voteEnd = new Date(CONTEST_DATES.VOTE_END_KST);
  if (now < submitEnd) return 'submission';
  if (now < voteEnd) return 'voting';
  return 'result';
};

/**
 * 단계별 UI 메타
 * ctaIconKey: 'document' | 'vote' | 'star' (contestIcons.js 참조)
 */
export const PHASE_META = {
  submission: {
    badgeLabel: '접수 중',
    badgeClass: 'contest-page__status-badge--submission',
    ctaLabel: '작품 제출하기',
    ctaIconKey: 'document',
  },
  voting: {
    badgeLabel: '투표 중',
    badgeClass: 'contest-page__status-badge--voting',
    ctaLabel: '투표하러 가기',
    ctaIconKey: 'vote',
  },
  result: {
    badgeLabel: '결과 발표',
    badgeClass: 'contest-page__status-badge--result',
    ctaLabel: '수상작 보기',
    ctaIconKey: 'star',
  },
};

/**
 * 목업 작품 (초등 5 / 중등 6 / 고등 5 / 특수 1 = 17개)
 * 제목·설명은 실제 교사가 쓸법한 현실적 톤
 *
 * 색상 절제 원칙(whatquiz-design §5, §382):
 * - 작품별 개별 색상·그라디언트 사용 금지
 * - 학교급만 공통 tint 로 구분 (elementary/middle/high/special — 4종)
 * - 실제 썸네일 UI 는 contestIcons.js 의 GradeIcon + 학교급별 배경 tint 조합
 */
export const MOCK_WORKS = [
  {
    id: 'w001',
    title: 'AI 생기부로 3월 첫주 퇴근시간 2시간 앞당긴 이야기',
    authorLabel: '서울 · 초등 5년차 김○○',
    gradeGroup: 'elementary',
    summary: '32명 반 학생들의 자율활동 생기부를 왓퀴즈로 초안 생성 후 검토·수정하여 평소 4시간 걸리던 작업을 1시간 50분만에 완료했습니다.',
    likeCount: 287,
    createdAtKst: '2026-09-04T14:22:00+09:00',
    tags: ['생기부', '자율활동', '효율'],
  },
  {
    id: 'w002',
    title: 'AI 학습지로 수준별 맞춤 문항 제작 — 상위권·중위권·하위권 3세트',
    authorLabel: '부산 · 초등 12년차 박○○',
    gradeGroup: 'elementary',
    summary: '같은 단원의 학습지를 3수준으로 분리 제작하여 맞춤 학습을 구현했습니다. 학생들의 학습 격차가 2주만에 눈에 띄게 줄었습니다.',
    likeCount: 231,
    createdAtKst: '2026-09-07T10:11:00+09:00',
    tags: ['학습지', '수준별', '맞춤학습'],
  },
  {
    id: 'w003',
    title: '공개수업 PPT를 30분만에 — AI PPT + 교과서 첨부 활용기',
    authorLabel: '대전 · 중등 국어 7년차 이○○',
    gradeGroup: 'middle',
    summary: '교과서 PDF를 첨부하면 핵심 내용을 추출해 PPT를 자동 생성합니다. 공개수업 준비에 평소 4시간 걸리던 작업이 30분으로 단축됐습니다.',
    likeCount: 224,
    createdAtKst: '2026-09-09T16:40:00+09:00',
    tags: ['PPT', '공개수업', '국어'],
  },
  {
    id: 'w004',
    title: '서술형 채점을 한 반에 12분 — 이제 퇴근 전에 끝냅니다',
    authorLabel: '인천 · 중등 영어 4년차 최○○',
    gradeGroup: 'middle',
    summary: '28명 반의 서술형 5문항을 채점 기준표 기반 AI 자동 채점으로 12분만에 완료. 수정·피드백 포함 35분만에 반 전체 채점이 끝났습니다.',
    likeCount: 198,
    createdAtKst: '2026-09-11T09:15:00+09:00',
    tags: ['서술형채점', '영어', '자동화'],
  },
  {
    id: 'w005',
    title: '학부모 상담 전 학생 기록 정리 — AI 어시스턴트 활용',
    authorLabel: '광주 · 초등 8년차 정○○',
    gradeGroup: 'elementary',
    summary: '학생별 관찰 기록과 상담 메모를 AI 어시스턴트에 요약시켜 학부모 상담 준비 시간을 절반으로 줄였습니다.',
    likeCount: 176,
    createdAtKst: '2026-09-13T11:00:00+09:00',
    tags: ['상담', '학부모', '기록정리'],
  },
  {
    id: 'w006',
    title: '고3 수시철 자기소개서 지도 — 학생별 맞춤 피드백 자동화',
    authorLabel: '대구 · 고등 진로 9년차 강○○',
    gradeGroup: 'high',
    summary: '학생 30명의 자기소개서를 AI 어시스턴트로 1차 피드백 후 개별 대면 상담에 집중. 예년 대비 지도 시간 40% 단축 + 지원 완성도 상승.',
    likeCount: 164,
    createdAtKst: '2026-09-15T13:50:00+09:00',
    tags: ['자기소개서', '진로', '수시'],
  },
  {
    id: 'w007',
    title: '특수학급 개별화교육계획(IEP) 작성 보조 — 학생 맥락 보존 AI',
    authorLabel: '경기 · 특수 11년차 윤○○',
    gradeGroup: 'special',
    summary: '학생의 장애 특성과 관찰 기록을 반영해 IEP 초안을 AI로 생성, 교사 전문성이 필요한 교육목표·평가 설계에 집중할 수 있었습니다.',
    likeCount: 152,
    createdAtKst: '2026-09-16T15:10:00+09:00',
    tags: ['IEP', '특수교육', '개별화'],
  },
  {
    id: 'w008',
    title: 'AI 이미지로 수업용 판서 자료 5분 완성',
    authorLabel: '울산 · 중등 사회 6년차 한○○',
    gradeGroup: 'middle',
    summary: '역사 수업에서 설명하기 어려운 시대별 생활상을 AI 이미지로 즉석 생성해 보여주니 학생들의 몰입도가 확연히 올라갔습니다.',
    likeCount: 138,
    createdAtKst: '2026-09-18T08:45:00+09:00',
    tags: ['AI이미지', '사회', '판서'],
  },
  {
    id: 'w009',
    title: '평가계획서 6종 일괄 작성 — 학기 초 부담 완전 해소',
    authorLabel: '경남 · 고등 수학 14년차 서○○',
    gradeGroup: 'high',
    summary: '수행평가·지필평가·공개수업 지도안 등 6종을 일괄 자동 작성. 학기 초 2주 걸리던 서류 업무를 3일만에 끝냈습니다.',
    likeCount: 127,
    createdAtKst: '2026-09-19T14:30:00+09:00',
    tags: ['평가계획서', '학기초', '수학'],
  },
  {
    id: 'w010',
    title: '동아리활동 생기부 32명 동시 생성 → 학급 일괄 다운로드',
    authorLabel: '충북 · 중등 진로 3년차 조○○',
    gradeGroup: 'middle',
    summary: '관찰 기록 CSV를 업로드하면 32명 생기부를 동시에 생성하고 나이스 엑셀 형식으로 일괄 다운로드 받을 수 있어 정말 편했습니다.',
    likeCount: 118,
    createdAtKst: '2026-09-20T10:05:00+09:00',
    tags: ['생기부', '동아리', '일괄생성'],
  },
  {
    id: 'w011',
    title: '공문 초안 작성에 9가지 전문 모드 활용하기',
    authorLabel: '전북 · 초등 부장 16년차 임○○',
    gradeGroup: 'elementary',
    summary: '학부모 소통·공문서·학생 상담 등 9가지 전문 모드로 목적에 맞는 문서 초안을 빠르게 작성. 결재 받기 전 수정 시간도 크게 줄었습니다.',
    likeCount: 106,
    createdAtKst: '2026-09-21T09:30:00+09:00',
    tags: ['공문', '부장업무', '전문모드'],
  },
  {
    id: 'w012',
    title: 'AI 채점 피드백을 학생이 즉시 확인 — QR 코드 제출의 힘',
    authorLabel: '제주 · 고등 국어 5년차 문○○',
    gradeGroup: 'high',
    summary: 'QR코드로 학생이 직접 제출 → AI 자동 채점 + 개별 피드백이 즉시 돌아갑니다. 학생 만족도가 크게 올라갔습니다.',
    likeCount: 97,
    createdAtKst: '2026-09-23T11:20:00+09:00',
    tags: ['QR제출', '자동채점', '피드백'],
  },
  {
    id: 'w013',
    title: '한글(HWP) 파일 직접 인식 — AI 3종 비교 후 왓퀴즈 정착기',
    authorLabel: '강원 · 중등 영어 10년차 황○○',
    gradeGroup: 'middle',
    summary: 'ChatGPT/Claude 에서 안 되던 HWP 파일 직접 인식이 되어 업무 흐름이 확 끊기지 않아 좋았습니다.',
    likeCount: 89,
    createdAtKst: '2026-09-24T16:00:00+09:00',
    tags: ['HWP', 'AI비교', '워크플로우'],
  },
  {
    id: 'w014',
    title: '교사용 AI 프롬프트 — 내 학교 맥락 이해도가 다르다',
    authorLabel: '세종 · 중등 과학 2년차 신○○',
    gradeGroup: 'middle',
    summary: '교사 업무 최적화 프롬프트가 내장되어 있어 "우리 학교의 맥락" 을 자연스럽게 이해합니다. 일반 AI와 체감이 완전히 달랐습니다.',
    likeCount: 76,
    createdAtKst: '2026-09-25T13:15:00+09:00',
    tags: ['프롬프트', '맥락이해', '과학'],
  },
  {
    id: 'w015',
    title: '학급 운영 팁을 생성 모델 3종 비교로 뽑아 쓰기',
    authorLabel: '충남 · 초등 1년차 오○○',
    gradeGroup: 'elementary',
    summary: 'GPT·Gemini·Claude 답변을 한 화면에서 비교하며 제일 맞는 조언을 골라 쓸 수 있어 신규 교사에게 큰 도움이 됐습니다.',
    likeCount: 68,
    createdAtKst: '2026-09-26T10:50:00+09:00',
    tags: ['신규교사', 'AI비교', '학급운영'],
  },
  {
    id: 'w016',
    title: '고등 수행평가 채점 기준표부터 피드백까지 — 완전 자동화',
    authorLabel: '경북 · 고등 사회 8년차 권○○',
    gradeGroup: 'high',
    summary: '채점 기준표 생성 → 학생 답안 AI 채점 → 개별 피드백까지 한 번에. 수행평가 채점이 평일 1시간 안에 끝났습니다.',
    likeCount: 57,
    createdAtKst: '2026-09-27T14:40:00+09:00',
    tags: ['수행평가', '채점기준', '자동화'],
  },
  {
    id: 'w017',
    title: '학부모 공개수업 후기 — AI PPT 슬라이드 만드는 교실',
    authorLabel: '전남 · 고등 정보 6년차 배○○',
    gradeGroup: 'high',
    summary: '학생들이 직접 AI PPT 를 활용해 발표 자료를 만드는 정보 수업을 공개했습니다. 학부모분들께 디지털 소양 교육의 방향을 직접 보여줄 수 있었습니다.',
    likeCount: 42,
    createdAtKst: '2026-09-28T11:25:00+09:00',
    tags: ['정보수업', '디지털소양', '공개수업'],
  },
];

/**
 * 학교급별 tint (아이콘 컨테이너 배경 + 아이콘 색)
 * whatquiz-design §382 "기능별 색상은 아이콘 컨테이너에만 허용"
 */
export const GRADE_TINT = {
  elementary: { bg: '#EFF6FF', fg: '#3182F6' },
  middle:     { bg: '#ECFDF5', fg: '#10B981' },
  high:       { bg: '#F5F3FF', fg: '#8B5CF6' },
  special:    { bg: '#FFF7ED', fg: '#D97706' },
};

/**
 * 학교급 필터 옵션
 */
export const GRADE_GROUPS = [
  { key: 'all', label: '전체' },
  { key: 'elementary', label: '초등' },
  { key: 'middle', label: '중등' },
  { key: 'high', label: '고등' },
  { key: 'special', label: '특수' },
];

export const GRADE_LABEL = {
  elementary: '초등',
  middle: '중등',
  high: '고등',
  special: '특수',
};

/**
 * 10월 일별 투표 추이 (목업)
 * 10/15~10/20 SNS 확산 피크
 */
export const MOCK_VOTE_TIMELINE = [
  { date: '10/01', votes: 18 }, { date: '10/02', votes: 24 }, { date: '10/03', votes: 21 },
  { date: '10/04', votes: 15 }, { date: '10/05', votes: 12 }, { date: '10/06', votes: 28 },
  { date: '10/07', votes: 35 }, { date: '10/08', votes: 31 }, { date: '10/09', votes: 27 },
  { date: '10/10', votes: 33 }, { date: '10/11', votes: 22 }, { date: '10/12', votes: 18 },
  { date: '10/13', votes: 42 }, { date: '10/14', votes: 56 }, { date: '10/15', votes: 78 },
  { date: '10/16', votes: 92 }, { date: '10/17', votes: 104 }, { date: '10/18', votes: 86 },
  { date: '10/19', votes: 69 }, { date: '10/20', votes: 81 }, { date: '10/21', votes: 58 },
  { date: '10/22', votes: 44 }, { date: '10/23', votes: 39 }, { date: '10/24', votes: 47 },
  { date: '10/25', votes: 52 }, { date: '10/26', votes: 38 }, { date: '10/27', votes: 29 },
  { date: '10/28', votes: 34 }, { date: '10/29', votes: 41 }, { date: '10/30', votes: 48 },
  { date: '10/31', votes: 63 },
];

/**
 * 목업 집계
 */
export const MOCK_CONTEST_STATS = {
  submissionCount: MOCK_WORKS.length,
  totalVotes: MOCK_VOTE_TIMELINE.reduce((s, d) => s + d.votes, 0),
  uniqueVoters: 312,
  todayVotes: 63,
};

/**
 * 시상 혜택 카드 (3개)
 * 대상 1명 · 최우수상 2명 · 우수상 3명 — 팀 확정안
 */
export const REWARDS = [
  {
    rank: '01',
    title: '대상',
    count: '1명',
    prize: '상금 50만원',
    description: '2026 충북 RISE 최우수 활용 사례',
  },
  {
    rank: '02',
    title: '최우수상',
    count: '2명',
    prize: '상금 30만원',
    description: '심사위원 고득점 사례',
  },
  {
    rank: '03',
    title: '우수상',
    count: '3명',
    prize: '상금 10만원',
    description: '심사위원 추천 사례',
  },
];

/**
 * 타임라인 스텝
 */
export const TIMELINE_STEPS = [
  { date: '2026.09.01', label: '접수 시작', description: '왓퀴즈 공모전 페이지에서 바로 제출' },
  { date: '2026.09.30', label: '접수 마감', description: '23:59 KST 까지 제출 완료' },
  { date: '2026.10.01', label: '투표 시작', description: '교사 커뮤니티 공개 투표 오픈' },
  { date: '2026.10.31', label: '투표 마감', description: '좋아요·댓글 기반 1차 후보 선정' },
  { date: '2026.11.14', label: '시상식', description: '최종 수상작 발표 및 시상' },
];

/**
 * 심사 기준
 */
export const CRITERIA = [
  { name: '창의성', weight: 40, description: '기존에 없던 새로운 활용 아이디어' },
  { name: '확산성', weight: 30, description: '다른 교사도 쉽게 따라할 수 있는 실용성' },
  { name: '구체성', weight: 30, description: '실제 수업·업무에 적용된 구체 사례와 데이터' },
];

/**
 * FAQ 10개
 */
export const FAQ_ITEMS = [
  {
    q: '꼭 왓퀴즈로 만든 사례여야 하나요?',
    a: '네, 본 공모전은 왓퀴즈 AI 기능을 활용한 교실·업무 개선 사례를 공유하는 자리입니다. 생기부·PPT·학습지·채점·이미지·AI 어시스턴트 등 어떤 기능이든 상관없어요.',
  },
  {
    q: '실증 참여 교사만 참여할 수 있나요?',
    a: '2026년 실증 참여 교사분들께 우선 공개되지만, 이후 전국 교사 대상으로 확장할 예정이에요. 자세한 일정은 공모전 페이지에서 확인할 수 있어요.',
  },
  {
    q: '어떤 형식으로 제출하나요?',
    a: '공모전 페이지의 "작품 제출하기" 버튼을 누르면 단계별로 안내해요. (1) 제목과 학교급 (2) 활용한 기능 (3) 적용 사례 500자 (4) 체감 효과·데이터 (5) 첨부 자료(선택)을 차례로 입력하면 됩니다.',
  },
  {
    q: '학생 정보가 포함되어도 괜찮나요?',
    a: '학생 실명·연락처·학번 등 개인정보는 절대 포함하지 말고, 반드시 익명화 처리 후 제출해 주세요. 본인 확인 가능한 교사 정보만 기입하시면 됩니다.',
  },
  {
    q: '투표는 누가 할 수 있나요?',
    a: '왓퀴즈에 가입한 모든 교사가 투표할 수 있어요. 비로그인 상태에서는 투표가 불가능하며, 투표 시도 시 가입 안내가 표시됩니다.',
  },
  {
    q: '하루에 몇 번 투표할 수 있나요?',
    a: '교사 1인당 하루 3표까지 가능하며, 같은 작품에는 하루 1회만 투표할 수 있어요. 당일 자정(KST)에 투표 가능 횟수가 초기화됩니다.',
  },
  {
    q: '첫 투표 시 혜택이 있다고 들었어요',
    a: '네, 공모전 기간 중 첫 투표를 하시면 왓퀴즈 PRO 1개월 연장 쿠폰을 자동으로 지급해요. 교사분들의 많은 관심과 참여를 유도하기 위함이에요.',
  },
  {
    q: '댓글이나 "따라해볼래요"는 어떻게 쓰나요?',
    a: '각 작품 페이지 하단에서 댓글을 남기고, "따라해볼래요" 버튼으로 관심 표시를 할 수 있어요. 다른 선생님의 사례를 응원하고 궁금한 점을 물어보세요. 작성자는 댓글에 직접 답할 수 있습니다.',
  },
  {
    q: '수상 결과는 어떻게 발표되나요?',
    a: '좋아요·댓글 수 기반 1차 후보(상위 30작) → 심사위원 패널의 최종 심사를 거쳐 11/14 시상식에서 발표돼요. 결과는 공모전 페이지와 이메일로 안내드립니다.',
  },
  {
    q: '저작권은 누구에게 있나요?',
    a: '저작권은 제출하신 교사분 본인에게 있으며, 본 공모전은 비상업적 사용 허락만 받아요. 2026 실증 결과 보고서나 보도 자료에 활용할 경우 사전 동의 후 진행합니다.',
  },
];

/**
 * 참여 방법 3단계
 * iconKey: contestIcons.js 참조
 */
export const HOW_TO_JOIN = [
  {
    step: 1,
    iconKey: 'target',
    title: '왓퀴즈 기능 활용',
    description: '실증 기간 중 왓퀴즈의 AI 기능을 실제 수업·업무에 적용해 보세요.',
  },
  {
    step: 2,
    iconKey: 'pencil',
    title: '사례 작성',
    description: '어떤 기능을 어떻게 활용했고, 어떤 변화가 있었는지 500자 이내로 정리해 주세요.',
  },
  {
    step: 3,
    iconKey: 'send',
    title: '바로 제출',
    description: '공모전 페이지 "작품 제출하기" 버튼을 눌러 단계별로 입력하면 끝. 심사 결과는 이메일로 안내드려요.',
  },
];

/* ═══════════════════════════════════════════
   투표 localStorage 시뮬레이션
   - 키: contest_votes_YYYY-MM-DD (KST)
   - 값: [workId, workId, ...]
   ═══════════════════════════════════════════ */

const getTodayKst = () => {
  const kst = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
  const yyyy = kst.getFullYear();
  const mm = String(kst.getMonth() + 1).padStart(2, '0');
  const dd = String(kst.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getVoteStorageKey = () => `contest_votes_${getTodayKst()}`;

/**
 * 오늘 남은 투표 수
 */
export const getRemainingVotes = () => {
  try {
    const raw = localStorage.getItem(getVoteStorageKey());
    const voted = raw ? JSON.parse(raw) : [];
    return Math.max(0, 3 - voted.length);
  } catch {
    return 3;
  }
};

/**
 * 오늘 이미 투표한 작품 ID 배열
 */
export const getTodayVotedWorkIds = () => {
  try {
    const raw = localStorage.getItem(getVoteStorageKey());
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * 오늘 이 작품에 투표한 적 있는지
 */
export const hasVotedForToday = (workId) => {
  return getTodayVotedWorkIds().includes(workId);
};

/**
 * 전체 공모전 기간 중 '첫 투표' 인지
 * (쿠폰 자동발급 시뮬용)
 */
const FIRST_VOTE_FLAG_KEY = 'contest_has_voted_ever';
export const hasEverVoted = () => {
  try {
    return localStorage.getItem(FIRST_VOTE_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

/* ═══════════════════════════════════════════
   작품 제출 localStorage 시뮬레이션
   - 내부 제출 시스템 (구글폼 대체)
   - 실제 Firestore 연동은 후속 PR
   ═══════════════════════════════════════════ */

const SUBMISSIONS_KEY = 'contest_my_submissions';

/** 내가 제출한 작품 목록 (최신순) */
export const getMySubmissions = () => {
  try {
    const raw = localStorage.getItem(SUBMISSIONS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

/** 작품 제출 저장 */
export const saveSubmission = (submission) => {
  try {
    const prev = getMySubmissions();
    const newSubmission = {
      id: `submitted_${Date.now()}`,
      ...submission,
      createdAtKst: new Date().toISOString(),
      status: 'pending', // pending | approved | hidden
      likeCount: 0,
      commentCount: 0,
    };
    const next = [newSubmission, ...prev];
    localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(next));
    return newSubmission;
  } catch {
    return null;
  }
};

/**
 * 왓퀴즈 기능 선택지 (작품 제출 시)
 * 아이콘 컨테이너용 tint 는 --whatquiz-primary 하나로 통일 (§5 색상 절제)
 */
export const FEATURE_OPTIONS = [
  { key: 'saenggibu', label: 'AI 생기부' },
  { key: 'ppt',       label: 'AI PPT'    },
  { key: 'worksheet', label: 'AI 학습지' },
  { key: 'scoring',   label: 'AI 서술형 채점' },
  { key: 'image',     label: 'AI 이미지' },
  { key: 'chat',      label: 'AI 채팅'   },
  { key: 'evaluation',label: '평가계획서' },
  { key: 'counseling',label: '학부모 소통' },
];

/* ═══════════════════════════════════════════
   댓글 / 북마크 / 공유 시뮬레이션
   트렌드 레퍼런스:
   - 쿠팡: 도움이 돼요 + 판매자 답변
   - 당근: 관심(북마크) + 매너 텍스트 뱃지
   - Threads: 좋아요/댓글/공유/북마크 좌측 정렬 minimal
   ═══════════════════════════════════════════ */

/**
 * 작품별 댓글 시드 (일부 작품만 구현)
 * isAuthor: true = 작성 교사 본인 답변 (쿠팡 판매자 답변 패턴)
 */
export const MOCK_COMMENTS = {
  w001: [
    { id: 'c_w001_1', authorLabel: '경기 · 초등 8년차 박○○', content: '저도 자율활동 생기부 작성 부담이 컸는데, 이 방식 꼭 시도해 볼게요. 혹시 학생별 특성 입력은 어떤 식으로 정리하셨나요?', createdAtKst: '2026-09-05T09:30:00+09:00', helpfulCount: 12, isAuthor: false },
    { id: 'c_w001_2', authorLabel: '서울 · 초등 5년차 김○○', content: '평소 쓰던 관찰 메모 엑셀을 그대로 붙여넣으면 AI가 학생별로 분리해서 초안을 써줘요. 처음 5명 정도는 수정 많지만, 그 다음부터는 제 톤을 학습하는 느낌이에요.', createdAtKst: '2026-09-05T10:12:00+09:00', helpfulCount: 24, isAuthor: true },
    { id: 'c_w001_3', authorLabel: '부산 · 초등 부장 15년차 이○○', content: '부장으로서 이런 사례를 동학년에 공유하고 싶네요. 발표자로 학년 회의 때 불러도 될까요?', createdAtKst: '2026-09-06T14:40:00+09:00', helpfulCount: 18, isAuthor: false },
  ],
  w002: [
    { id: 'c_w002_1', authorLabel: '광주 · 중등 수학 3년차 최○○', content: '수준별 학습지 너무 좋아보여요. 상위권/하위권 기준은 어떻게 나누셨어요?', createdAtKst: '2026-09-08T11:00:00+09:00', helpfulCount: 9, isAuthor: false },
    { id: 'c_w002_2', authorLabel: '부산 · 초등 12년차 박○○', content: '최근 단원평가 점수 분포를 기준으로 3분위로 나눴어요. AI한테 "중위권은 기본 이해는 되지만 응용에 약함" 같은 특성을 알려주면 맞춤 문항을 만들어줘요.', createdAtKst: '2026-09-08T12:20:00+09:00', helpfulCount: 16, isAuthor: true },
  ],
  w003: [
    { id: 'c_w003_1', authorLabel: '전북 · 중등 국어 7년차 한○○', content: '교과서 PDF 첨부가 정말 핵심이네요. 저희 학교 교과서도 해볼게요.', createdAtKst: '2026-09-10T10:00:00+09:00', helpfulCount: 7, isAuthor: false },
  ],
  w004: [
    { id: 'c_w004_1', authorLabel: '대구 · 중등 영어 9년차 조○○', content: '채점 기준표만 잘 만들어두면 정말 시간 절약이 크더라고요. 혹시 기준표 템플릿 공유 가능하실까요?', createdAtKst: '2026-09-12T15:10:00+09:00', helpfulCount: 14, isAuthor: false },
    { id: 'c_w004_2', authorLabel: '인천 · 중등 영어 4년차 최○○', content: '댓글에 남기기엔 길어서 이메일로 보내드릴게요. 프로필 메시지 주시면 기준표 예시 파일 드릴게요.', createdAtKst: '2026-09-12T16:00:00+09:00', helpfulCount: 19, isAuthor: true },
  ],
};

/** 작품별 댓글 목록 가져오기 (목업 + localStorage 추가분) */
const LOCAL_COMMENTS_KEY = (workId) => `contest_comments_${workId}`;

export const getCommentsFor = (workId) => {
  const seed = MOCK_COMMENTS[workId] || [];
  try {
    const raw = localStorage.getItem(LOCAL_COMMENTS_KEY(workId));
    const local = raw ? JSON.parse(raw) : [];
    return [...seed, ...(Array.isArray(local) ? local : [])];
  } catch {
    return seed;
  }
};

/** 작품별 '도움이 돼요' 상태 localStorage */
const HELPFUL_KEY = 'contest_helpful_comments';

const getHelpfulSet = () => {
  try {
    const raw = localStorage.getItem(HELPFUL_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

export const isHelpful = (commentId) => getHelpfulSet().has(commentId);

export const toggleHelpful = (commentId) => {
  try {
    const set = getHelpfulSet();
    const next = set.has(commentId);
    if (next) set.delete(commentId);
    else set.add(commentId);
    localStorage.setItem(HELPFUL_KEY, JSON.stringify([...set]));
    return !next; // true = 새로 '도움이 돼요' 함
  } catch {
    return false;
  }
};

/** 댓글 추가 (로그인 사용자만) */
export const addComment = (workId, content, authorLabel) => {
  try {
    const newComment = {
      id: `c_local_${Date.now()}`,
      authorLabel: authorLabel || '왓퀴즈 교사',
      content: content.trim(),
      createdAtKst: new Date().toISOString(),
      helpfulCount: 0,
      isAuthor: false,
    };
    const key = LOCAL_COMMENTS_KEY(workId);
    const raw = localStorage.getItem(key);
    const prev = raw ? JSON.parse(raw) : [];
    const next = [...prev, newComment];
    localStorage.setItem(key, JSON.stringify(next));
    return newComment;
  } catch {
    return null;
  }
};

/* ─── 따라해볼래요 (북마크) ─── */
const BOOKMARK_KEY = 'contest_bookmarks';

const getBookmarkSet = () => {
  try {
    const raw = localStorage.getItem(BOOKMARK_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

export const isBookmarked = (workId) => getBookmarkSet().has(workId);

export const toggleBookmark = (workId) => {
  try {
    const set = getBookmarkSet();
    const had = set.has(workId);
    if (had) set.delete(workId);
    else set.add(workId);
    localStorage.setItem(BOOKMARK_KEY, JSON.stringify([...set]));
    return !had; // true = 새로 북마크
  } catch {
    return false;
  }
};

/**
 * 작품별 기본 북마크/댓글 집계 베이스
 * 현실감을 위해 작품별로 약간 다른 숫자 시드
 */
export const getWorkStats = (workId) => {
  const seedComments = (MOCK_COMMENTS[workId] || []).length;
  const seedBookmark = Math.max(3, Math.floor((MOCK_WORKS.find(w => w.id === workId)?.likeCount || 0) * 0.18));
  let localCommentAdd = 0;
  try {
    const raw = localStorage.getItem(LOCAL_COMMENTS_KEY(workId));
    localCommentAdd = raw ? JSON.parse(raw).length : 0;
  } catch {}
  return {
    commentCount: seedComments + localCommentAdd,
    bookmarkCount: seedBookmark + (isBookmarked(workId) ? 1 : 0),
  };
};

/**
 * 투표 시도
 * @returns {{ ok: true, isFirstVote: boolean } | { ok: false, reason: 'duplicate'|'limit' }}
 */
export const castVote = (workId) => {
  try {
    const key = getVoteStorageKey();
    const raw = localStorage.getItem(key);
    const voted = raw ? JSON.parse(raw) : [];

    if (voted.includes(workId)) {
      return { ok: false, reason: 'duplicate' };
    }
    if (voted.length >= 3) {
      return { ok: false, reason: 'limit' };
    }

    voted.push(workId);
    localStorage.setItem(key, JSON.stringify(voted));

    const isFirstVote = !hasEverVoted();
    if (isFirstVote) {
      localStorage.setItem(FIRST_VOTE_FLAG_KEY, '1');
    }

    return { ok: true, isFirstVote };
  } catch {
    return { ok: false, reason: 'duplicate' };
  }
};
